/**
 * WebGL Animated Procedural Texture Background
 * Generates animated grain textures with blend modes using GLSL shaders
 */

class ProceduralTexture {
    constructor(container) {
        this.container = container;
        this.gl = null;
        this.program = null;
        this.isAnimating = false;
        this.startTime = performance.now();
        this.canvas = null;
    }

    async init() {
        // Check WebGL support
        const testCanvas = document.createElement('canvas');
        const gl = testCanvas.getContext('webgl') || testCanvas.getContext('experimental-webgl');

        if (!gl) {
            this.fallbackToCSS();
            return false;
        }


        try {
            // Create canvas
            this.canvas = document.createElement('canvas');
            this.canvas.width = window.innerWidth;
            this.canvas.height = window.innerHeight;
            this.canvas.className = 'webgl-grain-canvas';
            this.container.appendChild(this.canvas);

            // Get WebGL context
            this.gl = this.canvas.getContext('webgl') || this.canvas.getContext('experimental-webgl');

            if (!this.gl) {
                throw new Error('Failed to get WebGL context');
            }


            // Create shader program
            this.createShaderProgram();

            // Setup geometry (full screen quad)
            this.setupGeometry();

            // Get uniform locations
            this.uniforms = {
                time: this.gl.getUniformLocation(this.program, 'u_time'),
                resolution: this.gl.getUniformLocation(this.program, 'u_resolution')
            };

            // Handle resize
            window.addEventListener('resize', () => this.handleResize());

            // Start animation
            this.isAnimating = true;
            this.animate();

            return true;
        } catch (error) {
            this.fallbackToCSS();
            return false;
        }
    }

    createShaderProgram() {
        const gl = this.gl;

        // Vertex shader
        const vertexShaderSource = `
            attribute vec2 a_position;
            varying vec2 v_texCoord;

            void main() {
                gl_Position = vec4(a_position, 0.0, 1.0);
                v_texCoord = a_position * 0.5 + 0.5;
            }
        `;

        // Fragment shader with procedural noise
        const fragmentShaderSource = `
            precision highp float;

            uniform float u_time;
            uniform vec2 u_resolution;
            varying vec2 v_texCoord;

            // Hash functions for procedural noise
            float hash12(vec2 p) {
                vec3 p3 = fract(vec3(p.x, p.y, p.x) * 0.1031);
                p3 += dot(p3, vec3(p3.y, p3.z, p3.x) + 33.33);
                return fract((p3.x + p3.y) * p3.z);
            }

            vec2 hash22(vec2 p) {
                vec3 p3 = fract(vec3(p.x, p.y, p.x) * vec3(0.1031, 0.1030, 0.0973));
                p3 += dot(p3, vec3(p3.y, p3.z, p3.x) + 33.33);
                return fract((p3.xx + p3.yz) * p3.zy);
            }

            // Smooth noise
            float noise(vec2 p) {
                vec2 i = floor(p);
                vec2 f = fract(p);
                vec2 u = f * f * (3.0 - 2.0 * f);

                return mix(
                    mix(hash12(i + vec2(0.0, 0.0)),
                        hash12(i + vec2(1.0, 0.0)), u.x),
                    mix(hash12(i + vec2(0.0, 1.0)),
                        hash12(i + vec2(1.0, 1.0)), u.x),
                    u.y
                );
            }

            // Fractal Brownian Motion
            float fbm(vec2 p, int octaves) {
                float value = 0.0;
                float amplitude = 0.5;
                float frequency = 1.0;

                for (int i = 0; i < 8; i++) {
                    if (i >= octaves) break;
                    value += amplitude * noise(p * frequency);
                    frequency *= 2.0;
                    amplitude *= 0.5;
                }

                return value;
            }

            // Fine grain noise
            float grainNoise(vec2 p, float time) {
                return hash12(p * 1000.0 + time * 0.1);
            }

            // Larger grain noise (more visible individual grains)
            float largeGrainNoise(vec2 p, float time) {
                return hash12(p * 150.0 + time * 0.05);
            }

            // Voronoi cellular noise
            float voronoi(vec2 p) {
                vec2 i = floor(p);
                vec2 f = fract(p);

                float minDist = 1.0;

                for (int y = -1; y <= 1; y++) {
                    for (int x = -1; x <= 1; x++) {
                        vec2 neighbor = vec2(float(x), float(y));
                        vec2 point = hash22(i + neighbor);
                        vec2 diff = neighbor + point - f;
                        float dist = length(diff);
                        minDist = min(minDist, dist);
                    }
                }

                return minDist;
            }

            // Blend mode functions
            vec3 blendOverlay(vec3 base, vec3 blend) {
                return vec3(
                    base.r < 0.5 ? 2.0 * base.r * blend.r : 1.0 - 2.0 * (1.0 - base.r) * (1.0 - blend.r),
                    base.g < 0.5 ? 2.0 * base.g * blend.g : 1.0 - 2.0 * (1.0 - base.g) * (1.0 - blend.g),
                    base.b < 0.5 ? 2.0 * base.b * blend.b : 1.0 - 2.0 * (1.0 - base.b) * (1.0 - blend.b)
                );
            }

            vec3 blendSoftLight(vec3 base, vec3 blend) {
                return vec3(
                    blend.r < 0.5 ? 2.0 * base.r * blend.r + base.r * base.r * (1.0 - 2.0 * blend.r) : (2.0 * base.r - 1.0) * (blend.r - blend.r * blend.r) + base.r,
                    blend.g < 0.5 ? 2.0 * base.g * blend.g + base.g * base.g * (1.0 - 2.0 * blend.g) : (2.0 * base.g - 1.0) * (blend.g - blend.g * blend.g) + base.g,
                    blend.b < 0.5 ? 2.0 * base.b * blend.b + base.b * base.b * (1.0 - 2.0 * blend.b) : (2.0 * base.b - 1.0) * (blend.b - blend.b * blend.b) + base.b
                );
            }

            vec3 blendDarken(vec3 base, vec3 blend) {
                return min(base, blend);
            }

            void main() {
                vec2 uv = v_texCoord;
                vec2 coord = uv * u_resolution;

                // Balanced grain texture - visible but not overpowering
                // Should match the mockup grain level

                // ========== TEXTURE 1: Visible grain ==========
                float largeGrain = largeGrainNoise(coord, u_time * 0.5);
                float fineGrain1 = grainNoise(coord, u_time * 0.5);
                float fbm1 = fbm(uv * 2.0 + u_time * 0.02, 4);
                // Medium contrast range
                float tex1 = mix(0.25, 0.75, largeGrain * 0.7 + fineGrain1 * 0.2 + fbm1 * 0.1);

                // ========== TEXTURE 2: Soft clouds ==========
                vec2 cloudPos = uv * 1.5 + vec2(u_time * 0.01, u_time * 0.015);
                float cloud = fbm(cloudPos, 5);
                float grain2 = grainNoise(coord * 0.5, u_time * 0.3);
                float tex2 = mix(0.3, 0.7, cloud * 0.65 + grain2 * 0.35);

                // ========== TEXTURE 3: Subtle voronoi ==========
                vec2 voronoiPos = uv * 4.0 + vec2(sin(u_time * 0.05) * 0.5, cos(u_time * 0.03) * 0.5);
                float voronoiValue = voronoi(voronoiPos);
                float fbm3 = fbm(uv * 3.0 + u_time * 0.025, 3);
                float grain3 = grainNoise(coord * 0.4, u_time * 0.7);
                float tex3 = mix(0.35, 0.65, voronoiValue * 0.4 + fbm3 * 0.35 + grain3 * 0.25);

                // Composite with balanced weights
                float combined = tex1 * 0.55 + tex2 * 0.25 + tex3 * 0.2;

                // Mild contrast boost (less aggressive than before)
                combined = smoothstep(0.35, 0.65, combined);

                // Subtle vignette
                float vignetteStrength = 1.0 - length(uv - 0.5) * 0.4;
                combined *= mix(0.92, 1.0, vignetteStrength);

                // Moderate opacity - visible grain without washing out content
                vec3 color = vec3(combined);
                float opacity = 0.55; // Increased from 0.45 for better visibility on red

                gl_FragColor = vec4(color, opacity);
            }
        `;

        // Compile shaders
        const vertexShader = this.compileShader(gl.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = this.compileShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

        // Create program
        this.program = gl.createProgram();
        gl.attachShader(this.program, vertexShader);
        gl.attachShader(this.program, fragmentShader);
        gl.linkProgram(this.program);

        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
            const info = gl.getProgramInfoLog(this.program);
            throw new Error('Could not link WebGL program: ' + info);
        }

        gl.useProgram(this.program);
    }

    compileShader(type, source) {
        const gl = this.gl;
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);

        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            const info = gl.getShaderInfoLog(shader);
            const typeName = type === gl.VERTEX_SHADER ? 'vertex' : 'fragment';
            throw new Error(`Could not compile ${typeName} shader: ${info}`);
        }

        return shader;
    }

    setupGeometry() {
        const gl = this.gl;

        // Full screen quad
        const vertices = new Float32Array([
            -1.0, -1.0,
             1.0, -1.0,
            -1.0,  1.0,
             1.0,  1.0
        ]);

        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

        const positionLocation = gl.getAttribLocation(this.program, 'a_position');
        gl.enableVertexAttribArray(positionLocation);
        gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
    }

    animate() {
        if (!this.isAnimating) return;

        const gl = this.gl;
        const currentTime = (performance.now() - this.startTime) / 1000.0;

        // Clear and setup
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        // Enable blending for transparency
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

        // Update uniforms
        gl.uniform1f(this.uniforms.time, currentTime);
        gl.uniform2f(this.uniforms.resolution, this.canvas.width, this.canvas.height);

        // Draw
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        requestAnimationFrame(() => this.animate());
    }

    handleResize() {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;
    }

    fallbackToCSS() {
        // No fallback needed - WebGL is supported on 97%+ of browsers
        console.warn('WebGL not supported - grain texture will not be displayed');
    }

    destroy() {
        this.isAnimating = false;
        if (this.gl) {
            const loseContext = this.gl.getExtension('WEBGL_lose_context');
            if (loseContext) {
                loseContext.loseContext();
            }
        }
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProceduralTexture);
} else {
    initProceduralTexture();
}

function initProceduralTexture() {
    const container = document.querySelector('.wbgpu');
    if (container) {
        const proceduralTexture = new ProceduralTexture(container);
        proceduralTexture.init();
    } else {
    }
}
